/**
 * CopilotSidebar — right-hand conversation panel.
 *
 * Shows agent messages + user messages + lightweight tool-call status
 * lines. Accepts free-text input that posts a user.message to the agent
 * session. Minimal styling — the dashboard is the visual star; this is a
 * dense text column.
 */

import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { STATUS } from '../../hooks/useAgentSession';
import './CopilotSidebar.css';

const CopilotSidebar = forwardRef(({ status, messages, toolCalls, onSend, onReconnect }, ref) => {
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // Imperative surface for the parent (CampaignPlanReviewPage) to push content
  // from the left-side dashboard into the Copilot input. The dashboard's
  // row-level "@" button uses this to drop a JSON-Pointer reference (plus
  // the line item's displayName) into whatever draft the reviewer is
  // composing — without hijacking their existing text.
  useImperativeHandle(ref, () => ({
    insertReference(text) {
      if (!text) return;
      setDraft(prev => {
        const sep = prev.length === 0 || /\s$/.test(prev) ? '' : ' ';
        return prev + sep + text + ' ';
      });
      // Focus after the state update has flushed so the cursor lands in the
      // textarea with the new content visible.
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }
      });
    },
  }), []);

  // Merge messages + tool-call status lines into a single chronological feed.
  // Defensive filtering: drop entries with no usable text. Prevents leftover
  // "undefined…" lines from any past bug where a tool call had no name field.
  const feed = [
    ...messages
      .filter(m => m && typeof m.text === 'string' && m.text.length > 0)
      .map(m => ({ ts: m.ts, kind: m.kind, text: m.text })),
    ...toolCalls
      .filter(c => c && typeof c.name === 'string' && c.name.length > 0)
      .map(c => ({
        ts: c.ts,
        kind: 'tool_line',
        text: c.status === 'running'
          ? `${c.name}…`
          : `${c.name} ✓${c.result?.rejected ? ' (rejected)' : ''}`,
      })),
  ].sort((a, b) => a.ts - b.ts);

  // Show a processing indicator below the feed while the agent is working
  // on a response. Catches INITIALIZING (first-time session setup) and
  // STREAMING (agent thinking or running a tool). IDLE / WAITING_FOR_USER /
  // terminal states don't show it.
  //
  // Declared BEFORE the useEffect that references it to avoid a temporal
  // dead zone error in the dep array.
  const isProcessing =
    status === STATUS.INITIALIZING || status === STATUS.STREAMING;
  const processingText =
    status === STATUS.INITIALIZING ? 'Starting session' : 'Agent is working';

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [feed.length, isProcessing]);

  const handleSend = () => {
    const t = draft.trim();
    if (!t) return;
    onSend(t);
    setDraft('');
  };

  // Anthropic rejects user.message while the agent is waiting on a
  // custom-tool response (it expects user.custom_tool_result instead). Our
  // backend translates that 400 to a 409, but the cleaner UX is to disable
  // the input up-front so the reviewer doesn't try. Any toolCall entry
  // still in status=running means we're in that window.
  const toolPending = toolCalls.some(c => c.status === 'running');

  const inputDisabled =
    status === STATUS.TERMINATED
    || status === STATUS.FINALIZED
    || status === STATUS.INITIALIZING
    || toolPending;

  const inputPlaceholder =
    status === STATUS.TERMINATED || status === STATUS.FINALIZED
      ? 'Session ended'
      : status === STATUS.INITIALIZING
        ? 'Starting…'
        : toolPending
          ? 'Agent is running a tool — Stop to interrupt'
          : 'Ask the agent…';

  return (
    <div className="copilot">
      <div className="copilot-header">
        <span className="copilot-title">Agent Copilot</span>
        {onReconnect && (
          <button className="copilot-reconnect" onClick={onReconnect}>Reconnect</button>
        )}
      </div>

      <div className="copilot-feed" ref={scrollRef}>
        {feed.length === 0 && (
          <div className="copilot-placeholder">
            Ask anything about this plan — budgets, geos, provenance. Click any
            row in the dashboard for an explanation.
          </div>
        )}
        {feed.map((e, i) => <FeedItem key={i} entry={e} />)}
        {isProcessing && (
          <div className="copilot-processing" aria-live="polite">
            <span className="copilot-processing-label">{processingText}</span>
            <span className="copilot-processing-dots" aria-hidden="true">
              <span className="dot dot-1" />
              <span className="dot dot-2" />
              <span className="dot dot-3" />
            </span>
          </div>
        )}
      </div>

      <div className="copilot-input-row">
        <textarea
          ref={textareaRef}
          className="copilot-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={inputPlaceholder}
          rows={2}
          disabled={inputDisabled}
        />
        <button
          className="copilot-send"
          onClick={handleSend}
          disabled={inputDisabled || !draft.trim()}
        >Send</button>
      </div>

      <style>{`
        .copilot { display: flex; flex-direction: column; height: 100%; }
        .copilot-header {
          padding: 10px 14px; border-bottom: 1px solid #e0e0e0;
          display: flex; align-items: center; justify-content: space-between;
        }
        .copilot-title { font-size: 13px; font-weight: 600; }
        .copilot-reconnect {
          font-size: 11px; padding: 3px 8px; border: 1px solid #ccc;
          border-radius: 3px; background: white; cursor: pointer;
        }
        .copilot-feed {
          flex: 1; overflow-y: auto; padding: 12px 14px;
          display: flex; flex-direction: column; gap: 10px;
        }
        .copilot-placeholder { color: #888; font-size: 13px; padding: 10px 0; }
        .copilot-input-row {
          display: flex; gap: 6px; padding: 10px; border-top: 1px solid #e0e0e0;
        }
        .copilot-input {
          flex: 1; resize: none; font: inherit; padding: 6px 8px;
          border: 1px solid #d0d7de; border-radius: 4px; font-size: 13px;
        }
        .copilot-input:disabled { background: #f5f5f5; }
        .copilot-send {
          padding: 0 14px; background: #0969da; color: white; border: none;
          border-radius: 4px; cursor: pointer; font-size: 13px;
        }
        .copilot-send:disabled { background: #b0c4de; cursor: not-allowed; }
      `}</style>
    </div>
  );
});
CopilotSidebar.displayName = 'CopilotSidebar';

// Agent messages commonly arrive as markdown: **bold**, bullet lists, inline
// `code`, occasionally tables (via remark-gfm). Render them natively so the
// reviewer sees formatted text, not literal syntax.
// remarkPlugins = [remarkGfm] stays stable across renders (required — passing
// a fresh array on every render is a known react-markdown re-render hazard).
const MD_PLUGINS = [remarkGfm];
const MD_COMPONENTS = {
  // Constrain <p> to not force extra spacing inside an already-padded bubble.
  p: ({ node, ...props }) => <p className="fi-agent-p" {...props} />,
  // Open any link the agent renders in a new tab.
  a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />,
  // Tighten code styling so inline `backticks` look reasonable in a chat bubble.
  //
  // react-markdown v9+ removed the `inline` prop from the `code` component
  // override, so the previous `inline ? inline-class : block-class` ternary
  // treated every backtick as block (full-width dark box, one per line). v10
  // distinguishes block code by the `language-*` className that fenced blocks
  // carry — inline backticks don't get one.
  code: ({ className, children, ...props }) => {
    const isBlock = /\blanguage-/.test(className || '');
    return isBlock
      ? <code className={`fi-agent-code-block ${className}`} {...props}>{children}</code>
      : <code className="fi-agent-code-inline" {...props}>{children}</code>;
  },
};

const FeedItem = ({ entry }) => {
  if (entry.kind === 'agent_message') {
    return (
      <div className="fi-agent">
        <ReactMarkdown remarkPlugins={MD_PLUGINS} components={MD_COMPONENTS}>
          {entry.text || ''}
        </ReactMarkdown>
      </div>
    );
  }
  if (entry.kind === 'user_message') {
    return <div className="fi-user">{entry.text}</div>;
  }
  if (entry.kind === 'tool_line') {
    return <div className="fi-tool">{entry.text}</div>;
  }
  if (entry.kind === 'error') {
    return <div className="fi-error">Error: {entry.message}</div>;
  }
  return null;
};

export default CopilotSidebar;
